<template>
  <!-- XOL-67 (decision 188): the Markers table under an RTA map, the legacy Candidate site's columns (Name, Lat., Lng., Distance). One row per pin the map
       draws (lib/rtaOption.markerTableRows). A row and its pin highlight each other: hovering or focusing a row says `enter` (the map raises and bounces the
       pin), and the map's `pin-hover` comes back as `hoverKey` (the row is tinted). Only an extra marker has an x, and only where the caller allows it. -->
  <div class="rta-marker-table" data-markers="table" role="region" :aria-label="label">
    <h5 class="rta-marker-table__title"><i class="fa fa-map-marked-alt" aria-hidden="true"></i> Markers</h5>
    <small v-if="hint" :id="hintIdFrom || hintId" class="text-muted rta-marker-table__hint">{{ hint }}</small>

    <el-table
      :data="rows"
      size="small"
      row-key="key"
      :row-class-name="rowClass"
      @cell-mouse-enter="onCellEnter">

      <el-table-column label="Name" min-width="200">
        <template slot-scope="scope">
          <span
            v-if="scope && scope.row"
            class="rta-marker-table__name"
            tabindex="0"
            :data-key="scope.row.key"
            @focus="onFocus(scope.row)"
            @blur="onBlur"><img class="rta-marker-table__icon" :src="scope.row.icon" alt=""><b>{{ scope.row.name }}</b></span>
        </template>
      </el-table-column>

      <el-table-column label="Lat." prop="lat" min-width="120" />
      <el-table-column label="Lng." prop="lng" min-width="120" />

      <el-table-column label="Distance" min-width="190">
        <template slot-scope="scope">
          <span v-if="scope && scope.row && scope.row.metres !== null"><b>{{ scope.row.metres }} m</b> <span class="text-muted">from {{ scope.row.from }}</span></span>
        </template>
      </el-table-column>

      <el-table-column v-if="anyDeletable" label="" width="56" align="center">
        <template slot-scope="scope">
          <el-button
            v-if="scope && scope.row && scope.row.deletable"
            class="rta-marker-table__remove text-danger"
            type="text"
            size="mini"
            icon="fa fa-times-circle"
            :aria-label="'Remove the ' + scope.row.name + ' marker'"
            @click="$emit('remove', scope.row)" />
        </template>
      </el-table-column>
    </el-table>
  </div>
</template>

<script>

var uid = 0

export default {
  name: 'rta-marker-table',

  props: {
    // lib/rtaOption.markerTableRows
    rows: {
      type: Array,
      default: function () { return [] }
    },

    // The pin the map says is hovered: its row is tinted.
    hoverKey: {
      type: String,
      default: null
    },

    // A line over the table (the option map: where a new marker lands).
    hint: {
      type: String,
      default: ''
    },

    label: {
      type: String,
      default: 'Markers'
    },

    // The hint's id, when the caller points other controls at it (the option map's add buttons are described by it).
    hintIdFrom: {
      type: String,
      default: null
    }
  },

  data () {
    return {
      // The key of the row `enter` was last said for (null: none, or `leave` was said).
      current: null,
      hintId: 'rta-marker-table-hint-' + (++uid)
    }
  },

  watch: {
    // A row that goes (the x) takes the pointer's place with it: the next row under the pointer is a new `enter`.
    rows (list) {
      var current = this.current

      if (current !== null && !list.some(function (r) { return r.key === current })) {
        this.current = null
      }
    }
  },

  mounted () {
    // Element says `cell-mouse-leave` / `cell-mouse-enter` for every cell the pointer crosses, so a pointer moving along one row would end the highlight and
    // start it again (and the pin would bounce again). The highlight ends when the pointer leaves the table body, not when it leaves a cell.
    var body = this.$el.querySelector('.el-table__body-wrapper')

    if (body) {
      this._body = body
      body.addEventListener('mouseleave', this.onBodyLeave)
    }
  },

  beforeDestroy () {
    if (this._body) {
      this._body.removeEventListener('mouseleave', this.onBodyLeave)
    }
  },

  computed: {
    anyDeletable () {
      return this.rows.some(function (r) { return r.deletable })
    }
  },

  methods: {
    rowClass (arg) {
      return arg && arg.row && arg.row.key === this.hoverKey ? 'is-hl' : ''
    },

    // `enter` once per row: moving between the cells of a row says nothing more.
    start (row) {
      if (!row || row.key === this.current) {
        return
      }

      this.current = row.key
      this.$emit('enter', row)
    },

    stop () {
      if (this.current === null) {
        return
      }

      this.current = null
      this.$emit('leave')
    },

    onCellEnter (row) {
      this.start(row)
    },

    onBodyLeave () {
      this.stop()
    },

    onFocus (row) {
      this.start(row)
    },

    onBlur () {
      this.stop()
    }
  }
}
</script>

<style scoped>
.rta-marker-table { margin-top: 12px; }
.rta-marker-table__title { margin: 8px 0 4px; color: #909399; font-weight: normal; }
.rta-marker-table__hint { display: block; margin-bottom: 6px; font-size: 12px; }
.rta-marker-table__icon { height: 18px; margin-right: 6px; vertical-align: middle; }
.rta-marker-table__name:focus { outline: 2px solid #409EFF; outline-offset: 2px; }
.rta-marker-table >>> .el-table__row.is-hl > td { background: #ecf5ff; }
</style>
